import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TopNavbarComponent } from '../../../app/parkifly-bookings/organizations/components/top-navbar/top-navbar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { CheckboxComponent } from '../../../app/parkifly-bookings/components/checkbox/checkbox.component';
import { SelfServiceService } from '../../../app/services/self-service.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { BookingConfigService } from '../../../app/parkifly-bookings/services/booking-config.service';

@Component({
  selector: 'app-drive-in-form',
  imports: [TopNavbarComponent, TranslocoModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatInputModule, MatFormFieldModule, MatCheckboxModule, MatRadioModule, MatSelectModule, MatDatepickerModule, MatNativeDateModule, MatDialogModule, UpperCasePipe, CheckboxComponent, UpperCasePipe],
  templateUrl: './drive-in-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './drive-in-form.component.css'
})

export class DriveInFormComponent {

  isEditingRegNo: boolean = true;
  baseUrl: string = '';
  parkingToBeCreated: boolean = false;
  isLoading: boolean = false;
  correctParkingSite = '';
  driveInBookingForm = new FormGroup({
    registrationNumber: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(9), Validators.pattern(/^[A-Z0-9]{4,9}$/)]),
  });

  driveInInputs = {
    licensePlate: '',
    parkingFrom: '',
    parkingTo: '',
    totalPrice: '',
    parkingId: '',
    gateName: '',
    orderId: '',
    orgId: '',
    vehicleOwner: null
  };

  constructor(private selfServiceService: SelfServiceService,
    public router: Router,
    private dialog: MatDialog,
    private bookingConfigService: BookingConfigService
  ) { }

  ngOnInit() {
    let identityUrl = JSON.parse(environment.IDENTITY);
    this.baseUrl = identityUrl === '/' ? `${window.location.origin}/` : identityUrl;
    this.bookingConfigService.setOrganization('skavsta');
  }

  resetdriveInBookingForm() {
    this.driveInBookingForm.reset();
    this.parkingToBeCreated = false;
  }

  regToUpper(): void {
    const control = this.driveInBookingForm.get('registrationNumber');
    if (control?.value) {
      control.setValue(control.value.toUpperCase());
    }
  }

  submitForm() {
    this.isLoading = true;
    this.parkingToBeCreated = false;
    const licensePlate = this.driveInBookingForm.controls.registrationNumber.value;

    this.selfServiceService.driveUpAfterRegistration(licensePlate).then((result: any) => {
      this.isLoading = false;
      const status = result.status;
      if (status == 6) {
        this.openParkingNotFoundDialog(licensePlate);
      } else if (status == 3) {
        this.parkingToBeCreated = true;
        this.correctParkingSite = result.correctParkingSiteName;
        this.driveInInputs = {
          licensePlate: licensePlate,
          parkingFrom: result.parkingFrom,
          parkingTo: result.parkingTo,
          totalPrice: result.price?.amountIncludeVat,
          parkingId: result.parkingId,
          gateName: result.gateName,
          orderId: result.orderId,
          orgId: result.organizationId,
          vehicleOwner: result.vehicleOwner || null
        };
        this.goToDriveInBookingForm();
      }
    }).catch(() => {
      this.isLoading = false;
      this.openParkingNotFoundDialog(licensePlate);
    });
  }

  goToDriveInBookingForm() {
    const queryParams: any = {
      l: this.driveInBookingForm.get('registrationNumber')?.value,
      parkingId: this.driveInInputs.parkingId,
      gateName: this.driveInInputs.gateName,
      parkingFrom: this.driveInInputs.parkingFrom,
      parkingTo: this.driveInInputs.parkingTo,
      price: this.driveInInputs.totalPrice,
      orderId: this.driveInInputs.orderId,
      orgId: this.driveInInputs.orgId
    };
    if (this.driveInInputs.vehicleOwner) {
      queryParams.vehicleOwner = JSON.stringify(this.driveInInputs.vehicleOwner);
    }
    this.router.navigate(['/parkifly/drive-in-details-form'], {
      queryParams,
      state: {
        licensePlate: this.driveInBookingForm.get('registrationNumber')?.value,
        parkingId: this.driveInInputs.parkingId,
        gateName: this.driveInInputs.gateName,
        parkingFrom: this.driveInInputs.parkingFrom,
        parkingTo: this.driveInInputs.parkingTo,
        price: this.driveInInputs.totalPrice,
        orderId: this.driveInInputs.orderId,
        orgId: this.driveInInputs.orgId,
        vehicleOwner: this.driveInInputs.vehicleOwner
      }
    });
  }

  openParkingNotFoundDialog(licensePlate: string) {
    this.dialog.open(ParkingNotFoundDialog, {
      width: '400px',
      data: { licensePlate }
    });
  }

  resetForm() {
    this.driveInBookingForm.reset();
    this.parkingToBeCreated = false;
  }

}

@Component({
  selector: 'parking-not-found-dialog',
  template: `
    <div class="flex flex-col items-center text-center p-6">
      <div style="background: #FF6B6B; border-radius: 70%; padding: 16px 21px; margin-bottom: 15px;">
        <mat-icon style="color: white; font-size: 32px; width: 32px; height: 32px;">search_off</mat-icon>
      </div>
      <h2 mat-dialog-title style="margin: 0 0 12px 0; color: #D32F2F; font-weight: 600;">{{ 'DRIVE_IN_BOOKING.NO_PARKING_FOUND' | transloco }}</h2>
      <p style="margin: 0 0 24px 0; color: #666; line-height: 1.5;">
        {{ 'DRIVE_IN_BOOKING.POP_UP_MSG_1' | transloco }}
        <strong style="color: #D32F2F;">{{data.licensePlate}}</strong>.
        {{ 'DRIVE_IN_BOOKING.POP_UP_MSG_2' | transloco }}
      </p>
       <div mat-dialog-actions class="flex gap-3">
        <button mat-raised-button style="background-color: #FF6B6B; color: white;" (click)="close()">
          OK
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatButtonModule, MatIconModule, MatDialogModule, TranslocoModule]
})
export class ParkingNotFoundDialog {
  constructor(
    public dialogRef: MatDialogRef<ParkingNotFoundDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  close() {
    this.dialogRef.close();
  }
}
